import {createContext, Dispatch, FC, ReactNode, useReducer, useContext} from "react";
import {AppToaster} from "../utils/toasters/toaster";
import {Intent} from "@blueprintjs/core";
import log from "loglevel";
import {TDriverItem} from "../components/manifest/create-manifest/CreateManifestModal";

export type CreateManifestState = {
  selectedDrivers: TDriverItem[];
};

type OpenManifestDetailsPayload = {
  manifestDriverId: number | undefined;
  driverId: number | undefined;
};

type CreateManifestPayload = {
  mutate: any;
  date: string;
};

const InitialCreateManifestState: CreateManifestState = {
  selectedDrivers: []
};

export type CreateManifestActions =
  | {
      type: "SET_SELECTED_CREATE_MANIFESTS";
      payload: TDriverItem[];
    }
  | {type: "SET_MANIFEST_DETAILS_DRAWER_OPEN"; payload: OpenManifestDetailsPayload}
  | {type: "CLEAR_MANIFEST_DETAILS_STATE"}
  | {type: "SET_SELECTED_CREATE_MANIFEST"; payload: TDriverItem}
  | {type: "CLEAR_SELECTED_CREATE_MANIFESTS"}
  | {type: "CREATE_MANIFEST"; payload: CreateManifestPayload};

export const CreateManifestReducer = (
  state: CreateManifestState,
  action: CreateManifestActions
): CreateManifestState => {
  switch (action.type) {
    case "SET_SELECTED_CREATE_MANIFEST": {
      const index = state.selectedDrivers.findIndex((item) => item.driverId === action.payload.driverId);
      let selection = [];
      if (index > -1) {
        selection = state.selectedDrivers.filter((driver) => driver.driverId !== action.payload.driverId);
      } else {
        selection = [...state.selectedDrivers, action.payload];
      }
      return {
        ...state,
        selectedDrivers: selection
      };
    }

    case "CLEAR_SELECTED_CREATE_MANIFESTS": {
      return {
        ...state,
        selectedDrivers: []
      };
    }

    case "CREATE_MANIFEST": {
      const manifestDate = action.payload.date;

      const promises: Promise<any>[] = [];

      state.selectedDrivers.forEach((driver) => {
        promises.push(
          action.payload.mutate({
            variables: {
              driverId: driver.driverId,
              siteId: driver.siteId,
              driverCode: driver.driverCode,
              dispatchGroupId: driver.dispatchGroupId,
              vehicleTypeId: driver.vehicleTypeId,
              startZone: driver.startZone,
              startZip: driver.startZip,
              endZip: driver.endZip,
              autoSend: driver.autoSend,
              autoAccept: driver.autoAccept,
              manifestDate: manifestDate,
              manifestDriverStatus: driver.manifestDriverStatus
            }
          })
        );
      });

      const driversListMsg = state.selectedDrivers.map((x) => `${x.driverCode}-${x.name}`).join(", ");
      Promise.allSettled(promises)
        .then(() => {
          AppToaster.show({
            intent: Intent.SUCCESS,
            icon: "tick",
            message: `Created${state.selectedDrivers.length > 1 ? "" : " a"} Manifest${
              state.selectedDrivers.length > 1 ? "s" : ""
            } for ${driversListMsg}`
          });
        })
        .catch((e: any) => {
          log.error(`Create Manifest Failed for Drivers Error: ${e}`);
          AppToaster.show({
            intent: Intent.DANGER,
            icon: "warning-sign",
            message: `Could not create${state.selectedDrivers.length > 1 ? "" : " a"} Manifest${
              state.selectedDrivers.length > 1 ? "s" : ""
            } for ${driversListMsg}`
          });
        });

      return {
        ...state,
        selectedDrivers: []
      };
    }
    default:
      return state;
  }
};

export const CreateManifestStateContext = createContext<CreateManifestState | undefined>(undefined);
export const CreateManifestDispatchContext = createContext<Dispatch<CreateManifestActions> | undefined>(undefined);

export const CreateManifestProvider: FC<{children: ReactNode}> = ({children}) => {
  const [createManifestState, createManifestStateDispatch] = useReducer(
    CreateManifestReducer,
    InitialCreateManifestState
  );

  return (
    <CreateManifestStateContext.Provider value={createManifestState}>
      <CreateManifestDispatchContext.Provider value={createManifestStateDispatch}>
        {children}
      </CreateManifestDispatchContext.Provider>
    </CreateManifestStateContext.Provider>
  );
};

export const useCreateManifestState = () => {
  const context = useContext(CreateManifestStateContext);
  if (!context) {
    throw new Error("useCreateManifestState must be used within a CreateManifestProvider");
  }
  return context;
};

export const useCreateManifestDispatch = () => {
  const context = useContext(CreateManifestDispatchContext);
  if (!context) {
    throw new Error("useCreateManifestDispatch must be used within a CreateManifestProvider");
  }
  return context;
};
